import React, { useState } from "react";
import { Card, Form, Button, Col, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { showSuccessToast } from "utils/toast";
import { useDispatch } from "react-redux";

import {
  addRequestLeave as addToStore,
  deleteRequestLeave as deleteFromStore,
} from "state/slices/requestleave/requestLeaveSlice";

import {
  useCreateRequestLeaveMutation,
  useDeleteRequestLeaveMutation,
} from "state/slices/requestleave/requestLeaveApiSlice";

import { LEAVE_TYPES, NOT_REQUIRE_END_DATE } from "constants";

const initialValues = {
  leaveType: "",
  leaveDate: "",
  endDate: "",
  reason: "",
};

const statusInitialValues = {
  id: null,
  status: "",
  ready: false,
};

const GeneralAccessManagement = React.memo(
  ({
    isAllowedToRead,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    RequestLeaveViewManagement,
    selectedMonthYear,
    setSelectedMonthYear,
  }) => {
    const dispatch = useDispatch();

    const [selectedRow, setSelectedRow] = useState(null);
    const [statusFormikValues, setStatusFormikValues] = useState(statusInitialValues);

    const [createRequestLeave] = useCreateRequestLeaveMutation();
    const [deleteRequestLeave] = useDeleteRequestLeaveMutation();

    // Event Handlers
    const handleCheckboxChange = (event, row) => {
      if (event.target.checked) {
        setSelectedRow(row);
      } else {
        setSelectedRow(null);
        setStatusFormikValues(statusInitialValues);
      }
    };

    const handleEdit = () => {
      if (selectedRow) {
        setStatusFormikValues({ ...selectedRow, ready: true });
      }
    };

    const handleSubmit = async (values, { resetForm }) => {
      try {
        const newLeave = await createRequestLeave(values).unwrap();

        const leaveDate = new Date(values.leaveDate);
        const month = leaveDate.getMonth() + 1;
        const year = leaveDate.getFullYear();

        if (selectedMonthYear.month === month && selectedMonthYear.year === year) {
          dispatch(addToStore(newLeave));
        }

        showSuccessToast("Request leave added successfully");

        resetForm();
        setSelectedRow(null);
        setStatusFormikValues(statusInitialValues);
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const deleteRequestLeaveManage = async () => {
      if (!selectedRow?.id) return;

      try {
        await deleteRequestLeave(selectedRow.id).unwrap();
        dispatch(deleteFromStore(selectedRow.id));
        showSuccessToast("Request leave deleted successfully");
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }

      setSelectedRow(null);
      setStatusFormikValues(statusInitialValues);
    };

    // Validation Schema
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const validationSchema = Yup.object().shape({
      leaveType: Yup.string()
        .oneOf(Object.keys(LEAVE_TYPES))
        .required("Leave type is required"),
      leaveDate: Yup.date()
        .min(today, "Leave date must be today or later")
        .required("Leave date is required"),
      endDate: Yup.date().when("leaveType", {
        is: (type) => !NOT_REQUIRE_END_DATE.includes(type),
        then: (schema) =>
          schema
            .min(Yup.ref("leaveDate"), "End date must be after leave date")
            .required("End date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      reason: Yup.string()
        .max(200, "Reason cannot exceed 200 characters")
        .required("Reason is required"),
    });

    return (
      <section className="section dashboard">
        {/* Form Section */}
        {isAllowedToCreate && !statusFormikValues.ready && (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  handleChange,
                  handleSubmit,
                  touched,
                  errors,
                  setFieldValue,
                  isSubmitting,
                }) => {
                  const showEndDate =
                    values.leaveType &&
                    !NOT_REQUIRE_END_DATE.includes(values.leaveType);

                  return (
                    <Form
                      noValidate
                      onSubmit={handleSubmit}
                      className="row row-gap-4 mt-3"
                    >
                      {/* Leave Type */}
                      <Col md={3}>
                        <Form.Group controlId="leaveType">
                          <Form.Control
                            as="select"
                            name="leaveType"
                            value={values.leaveType}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.value === "casualHalfDay") {
                                setFieldValue("endDate", "");
                              }
                            }}
                            isInvalid={touched.leaveType && !!errors.leaveType}
                            disabled={isSubmitting}
                          >
                            <option value="">Select leave type</option>
                            {Object.entries(LEAVE_TYPES).map(([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            ))}
                          </Form.Control>
                          <Form.Control.Feedback type="invalid">
                            {errors.leaveType}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      {/* Leave Date */}
                      <Col md={3}>
                        <Form.Group controlId="leaveDate">
                          <Form.Control
                            type="date"
                            name="leaveDate"
                            value={values.leaveDate}
                            onChange={handleChange}
                            isInvalid={touched.leaveDate && !!errors.leaveDate}
                            disabled={isSubmitting}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.leaveDate}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      {/* End Date */}
                      {showEndDate && (
                        <Col md={3}>
                          <Form.Group controlId="endDate">
                            <Form.Control
                              type="date"
                              name="endDate"
                              value={values.endDate}
                              onChange={handleChange}
                              isInvalid={touched.endDate && !!errors.endDate}
                              disabled={isSubmitting}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.endDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}

                      {/* Submit Button */}
                      <Col md={3}>
                        <Button
                          variant="primary"
                          type="submit"
                          className="w-100"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                                style={{ width: "18px", height: "18px" }}
                              />
                              Sending...
                            </>
                          ) : (
                            "Send Request"
                          )}
                        </Button>
                      </Col>

                      {/* Reason */}
                      <Col md={12}>
                        <Form.Group controlId="reason">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="reason"
                            placeholder="Enter reason"
                            value={values.reason}
                            onChange={handleChange}
                            isInvalid={touched.reason && !!errors.reason}
                            disabled={isSubmitting}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.reason}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Form>
                  );
                }}
              </Formik>
            </Card.Body>
          </Card>
        )}
        {/* View Section */}
        {isAllowedToRead && (
          <RequestLeaveViewManagement
            isAllowedToUpdate={isAllowedToUpdate}
            isAllowedToDelete={isAllowedToDelete}
            selectedMonthYear={selectedMonthYear}
            setSelectedMonthYear={setSelectedMonthYear}
            handleCheckboxChange={handleCheckboxChange}
            handleEdit={handleEdit}
            deleteRequestLeaveManage={deleteRequestLeaveManage}
            selectedRow={selectedRow}
            statusData={statusFormikValues}
            onStatusUpdate={() => {
              setStatusFormikValues(statusInitialValues);
              setSelectedRow(null);
            }}
          />
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
